import React, { Fragment, useMemo, useRef, useState } from 'react';
import {
  Box,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import { DictionaryWord } from 'app/types';
import { TableColumnsVisibility } from 'components/tableSettings';
import AudioButton from './AudioButton';
import POSTag from './POSTag';
import StatusChip from './StatusChip';
import { pickStatusColor, statusOf } from './statusMeta';
import { UseWordsViewState } from './useWordsView';

interface DictionaryWordsViewProps {
  view: UseWordsViewState;
  /**
   * Per-column visibility for table mode. Cards mode shows every field —
   * the toggles only affect the columnar layout.
   *
   * Always-on columns: select, number, word, status, expand. The toggleable
   * ones — transcription, translation, definition — fall back to "shown"
   * when the prop is omitted.
   */
  columns?: TableColumnsVisibility;
}

/**
 * Column metadata. `min` feeds the table's `min-width` so it scrolls
 * horizontally on narrow viewports instead of squashing.
 */
interface ColSpec {
  key:
    | 'select'
    | 'number'
    | 'word'
    | 'transcription'
    | 'translation'
    | 'partOfSpeech'
    | 'definition'
    | 'status'
    | 'expand';
  label: string;
  width: string;
  min: number;
  align?: 'center';
}

const hexToRgba = (hex: string, a: number) => {
  const h = hex.replace('#', '');
  return `rgba(${parseInt(h.slice(0, 2), 16)}, ${parseInt(
    h.slice(2, 4),
    16,
  )}, ${parseInt(h.slice(4, 6), 16)}, ${a})`;
};

const ALL_COLS: ColSpec[] = [
  { key: 'select', label: '', width: '44px', min: 44, align: 'center' },
  { key: 'number', label: '№', width: '48px', min: 48 },
  { key: 'word', label: 'Слово', width: 'minmax(190px,1fr)', min: 190 },
  { key: 'transcription', label: 'Транскрипція', width: '130px', min: 130 },
  { key: 'translation', label: 'Переклад', width: '150px', min: 150 },
  { key: 'partOfSpeech', label: 'Частина мови', width: '120px', min: 120 },
  { key: 'definition', label: 'Визначення', width: 'minmax(260px,1.4fr)', min: 260 },
  { key: 'status', label: 'Статус', width: '152px', min: 152 },
  { key: 'expand', label: '', width: '40px', min: 40, align: 'center' },
];

const buildVisibleCols = (
  columns: TableColumnsVisibility | undefined,
): ColSpec[] => {
  const tr = columns?.transcription ?? true;
  const tl = columns?.translation ?? true;
  const pos = columns?.partOfSpeech ?? true;
  const def = columns?.definition ?? true;
  return ALL_COLS.filter((c) => {
    if (c.key === 'transcription') return tr;
    if (c.key === 'translation') return tl;
    if (c.key === 'partOfSpeech') return pos;
    if (c.key === 'definition') return def;
    return true;
  });
};

const tableMinWidth = (cols: ColSpec[]) =>
  cols.reduce((sum, c) => sum + c.min, 0);

const Checkbox: React.FC<{
  checked: boolean;
  /** Render an indeterminate (`-`) state — partial selection in a group. */
  indeterminate?: boolean;
  onClick: (e: React.MouseEvent) => void;
}> = ({ checked, indeterminate, onClick }) => {
  const filled = checked || indeterminate;
  return (
    <Box
      onClick={(e) => {
        e.stopPropagation();
        onClick(e);
      }}
      sx={{
        alignItems: 'center',
        bgcolor: filled ? 'primary.main' : 'transparent',
        border: '2px solid',
        borderColor: filled ? 'primary.main' : 'primary.tableBorder',
        borderRadius: '5px',
        cursor: 'pointer',
        display: 'inline-flex',
        flex: 'none',
        height: 20,
        justifyContent: 'center',
        transition: 'all .12s ease',
        width: 20,
      }}
    >
      {checked && !indeterminate && (
        <Box
          component={'svg'}
          viewBox={'0 0 16 16'}
          sx={{ color: 'common.white', height: 14, width: 14 }}
        >
          <path
            d={'M3 8.5l3 3 7-7'}
            fill={'none'}
            stroke={'currentColor'}
            strokeWidth={2.4}
            strokeLinecap={'round'}
            strokeLinejoin={'round'}
          />
        </Box>
      )}
      {indeterminate && (
        <Box
          sx={{
            bgcolor: 'common.white',
            borderRadius: 1,
            height: '3px',
            width: '10px',
          }}
        />
      )}
    </Box>
  );
};

const HeaderRow: React.FC<{
  view: UseWordsViewState;
  cols: ColSpec[];
  template: string;
}> = ({ view, cols, template }) => (
  <Box
    sx={{
      alignItems: 'center',
      bgcolor: 'primary.tableHeadBg',
      borderBottom: '1px solid',
      borderColor: 'primary.tableBorder',
      display: 'grid',
      gridTemplateColumns: template,
      height: 46,
      px: 1,
    }}
  >
    {cols.map((c) => {
      if (c.key === 'select') {
        return (
          <Stack key={c.key} direction={'row'} justifyContent={'center'}>
            <Checkbox
              checked={view.allSelected}
              onClick={() => view.toggleAll()}
            />
          </Stack>
        );
      }
      return (
        <Typography
          key={c.key}
          sx={{
            color: 'text.secondary',
            fontSize: 12,
            fontWeight: 800,
            letterSpacing: '0.04em',
            px: 1.5,
            textAlign: c.align ?? 'left',
            textTransform: 'uppercase',
          }}
        >
          {c.label}
        </Typography>
      );
    })}
  </Box>
);

/**
 * Letter group head — entire row is clickable to toggle all words on that
 * letter at once. Shows a checkbox with `indeterminate` state when partially
 * selected, plus a counter "N слів · M обрано".
 */
const GroupHeader: React.FC<{
  letter: string;
  ids: number[];
  view: UseWordsViewState;
}> = ({ letter, ids, view }) => {
  const selCount = ids.filter((id) => view.selected[id]).length;
  const allSelected = selCount === ids.length && ids.length > 0;
  const partial = selCount > 0 && !allSelected;

  return (
    <Box
      onClick={() => view.toggleMany(ids)}
      sx={(t) => ({
        alignItems: 'center',
        bgcolor: allSelected
          ? t.palette.mode === 'light'
            ? 'rgba(59,44,147,0.07)'
            : 'rgba(155,124,255,0.14)'
          : 'background.default',
        borderBottom: '1px solid',
        borderColor: 'primary.tableBorder',
        cursor: 'pointer',
        display: 'flex',
        gap: 1.5,
        pl: 2,
        pr: 2,
        py: 1,
        transition: 'background-color .12s ease',
        '&:hover': {
          bgcolor:
            t.palette.mode === 'light'
              ? 'rgba(59,44,147,0.07)'
              : 'rgba(155,124,255,0.14)',
        },
      })}
    >
      <Checkbox
        checked={allSelected}
        indeterminate={partial}
        onClick={() => view.toggleMany(ids)}
      />
      <Typography
        sx={{
          color: allSelected ? 'primary.main' : 'text.primary',
          fontSize: 14,
          fontWeight: 800,
          letterSpacing: '0.02em',
        }}
      >
        {letter}
      </Typography>
      <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
        {`${ids.length} ${view.sliv(ids.length)}${
          selCount > 0 ? ` · ${selCount} обрано` : ''
        }`}
      </Typography>
    </Box>
  );
};

/**
 * Definition cell with a tooltip that fires only when the visible text is
 * actually truncated by the 2-line clamp. This mirrors the prototype's
 * `DefinitionCell` behaviour — no popping for short definitions.
 */
const DefinitionCell: React.FC<{ text: string }> = ({ text }) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [truncated, setTruncated] = useState(false);

  const onEnter = () => {
    const el = ref.current;
    if (!el) return;
    setTruncated(el.scrollHeight > el.clientHeight + 1);
  };

  const cellEl = (
    <Typography
      ref={ref}
      onMouseEnter={onEnter}
      sx={{
        color: 'text.secondary',
        cursor: truncated ? 'help' : 'default',
        display: '-webkit-box',
        fontSize: 14,
        lineHeight: 1.4,
        overflow: 'hidden',
        px: 1.5,
        py: 1,
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: 2,
      }}
    >
      {text}
    </Typography>
  );

  if (!truncated) return cellEl;

  return (
    <Tooltip
      title={
        <Box sx={{ fontSize: 13, lineHeight: 1.5, maxWidth: 380 }}>{text}</Box>
      }
      placement={'top'}
      arrow
    >
      {cellEl}
    </Tooltip>
  );
};

const TableRow: React.FC<{
  word: DictionaryWord;
  index: number;
  selected: boolean;
  onToggle: () => void;
  cols: ColSpec[];
  template: string;
}> = ({ word, index, selected, onToggle, cols, template }) => {
  const [open, setOpen] = useState(false);
  const theme = useTheme();
  const status = statusOf(word);
  const statusColor = pickStatusColor(status, theme.palette.mode);
  const isDark = theme.palette.mode === 'dark';
  const canExpand = !!word.definition;

  // Row background tint matches the card-mode treatment — status colour
  // washed at low opacity so rows read as a coloured stripe at a glance.
  // 'new' stays neutral; the absence of tint IS the cue.
  const baseBg =
    status === 'new'
      ? undefined
      : hexToRgba(statusColor, isDark ? 0.12 : 0.08);
  const hoverBg =
    status === 'new'
      ? undefined
      : hexToRgba(statusColor, isDark ? 0.18 : 0.13);

  const renderCell = (col: ColSpec) => {
    switch (col.key) {
      case 'select':
        return (
          <Stack key={col.key} direction={'row'} justifyContent={'center'}>
            <Checkbox checked={selected} onClick={onToggle} />
          </Stack>
        );
      case 'number':
        return (
          <Typography
            key={col.key}
            sx={{ color: 'text.secondary', fontSize: 14, px: 1.5 }}
          >
            {index + 1}
          </Typography>
        );
      case 'word':
        return (
          <Stack
            key={col.key}
            direction={'row'}
            alignItems={'center'}
            spacing={1.125}
            sx={{ minWidth: 0, px: 1.5, py: 1 }}
          >
            <AudioButton word={word.word ?? ''} size={32} />
            <Typography
              sx={{
                fontSize: 16,
                fontWeight: 700,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {word.partOfSpeech === 'VERB' ? `to ${word.word}` : word.word}
            </Typography>
          </Stack>
        );
      case 'transcription':
        return (
          <Typography
            key={col.key}
            sx={{
              color: 'text.secondary',
              fontFamily: 'ui-monospace, monospace',
              fontSize: 14,
              px: 1.5,
            }}
          >
            {word.transcription}
          </Typography>
        );
      case 'translation':
        return (
          <Typography key={col.key} sx={{ fontSize: 14.5, px: 1.5 }}>
            {word.translation}
          </Typography>
        );
      case 'partOfSpeech':
        return (
          <Box key={col.key} sx={{ px: 1.5 }}>
            <POSTag pos={word.partOfSpeech} />
          </Box>
        );
      case 'definition':
        return (
          <Box key={col.key}>
            <DefinitionCell text={word.definition ?? ''} />
          </Box>
        );
      case 'status':
        // Prototype rule: status cell stays empty for `new` words — the row
        // is already visually distinct (transparent left bar), no chip needed.
        return (
          <Box key={col.key} sx={{ px: 1.5 }}>
            {status === 'new' ? null : (
              <StatusChip status={status} onClick={(e) => e.stopPropagation()} />
            )}
          </Box>
        );
      case 'expand':
        return (
          <Stack key={col.key} direction={'row'} justifyContent={'center'}>
            <Box
              component={'button'}
              onClick={(e) => {
                e.stopPropagation();
                if (canExpand) setOpen((o) => !o);
              }}
              title={'Приклад'}
              disabled={!canExpand}
              sx={{
                alignItems: 'center',
                background: 'transparent',
                border: 'none',
                borderRadius: '50%',
                color: 'text.secondary',
                cursor: canExpand ? 'pointer' : 'default',
                display: 'inline-flex',
                height: 30,
                justifyContent: 'center',
                opacity: canExpand ? 1 : 0.3,
                width: 30,
              }}
            >
              <ExpandMoreIcon
                sx={{
                  fontSize: 20,
                  transform: open ? 'rotate(180deg)' : 'none',
                  transition: 'transform .2s ease',
                }}
              />
            </Box>
          </Stack>
        );
      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        borderBottom: '1px solid',
        borderColor: 'primary.tableBorder',
      }}
    >
      <Box
        onClick={onToggle}
        sx={(t) => ({
          alignItems: 'center',
          bgcolor: selected
            ? t.palette.mode === 'light'
              ? 'rgba(59,44,147,0.06)'
              : 'rgba(155,124,255,0.12)'
            : baseBg ?? 'background.paper',
          cursor: 'pointer',
          display: 'grid',
          gridTemplateColumns: template,
          minHeight: 56,
          px: 1,
          transition:
            'background-color .12s ease, box-shadow .12s ease',
          '&:hover': {
            bgcolor: selected
              ? t.palette.mode === 'light'
                ? 'rgba(59,44,147,0.10)'
                : 'rgba(155,124,255,0.16)'
              : hoverBg ?? 'background.default',
            // Inner primary ring — confirms the row is interactive without
            // changing the cell layout (outline would shift content). Color
            // baked in directly because CSS box-shadow doesn't have a
            // separate `color` property.
            boxShadow: `inset 0 0 0 1.5px ${t.palette.primary.main}`,
          },
        })}
      >
        {cols.map(renderCell)}
      </Box>
      {open && word.definition && (
        <Stack
          direction={'row'}
          spacing={1.25}
          alignItems={'flex-start'}
          sx={{
            bgcolor:
              status === 'new'
                ? 'background.default'
                : hexToRgba(statusColor, isDark ? 0.08 : 0.05),
            pl: 8,
            pr: 2,
            py: 1.5,
          }}
        >
          <FormatQuoteIcon
            sx={{ color: 'primary.main', flex: 'none', fontSize: 18, mt: '2px' }}
          />
          <Box>
            <Typography
              sx={{
                color: 'text.secondary',
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: '0.06em',
                mb: 0.5,
                textTransform: 'uppercase',
              }}
            >
              {'Визначення'}
            </Typography>
            <Typography sx={{ fontSize: 15, fontStyle: 'italic' }}>
              {word.definition}
            </Typography>
          </Box>
        </Stack>
      )}
    </Box>
  );
};

/**
 * Stacked compact row for the phone breakpoint. Replaces the grid table —
 * the columnar layout is unusable at <480px because there's no horizontal
 * space, and a horizontal scroll on the entire page would be worse.
 */
const MobileRow: React.FC<{
  word: DictionaryWord;
  selected: boolean;
  onToggle: () => void;
  showTranscription: boolean;
  showDefinition: boolean;
}> = ({
  word,
  selected,
  onToggle,
  showTranscription,
  showDefinition,
}) => {
  const [open, setOpen] = useState(false);
  const theme = useTheme();
  const status = statusOf(word);
  const statusColor = pickStatusColor(status, theme.palette.mode);
  const isDark = theme.palette.mode === 'dark';
  const baseBg =
    status === 'new'
      ? undefined
      : hexToRgba(statusColor, isDark ? 0.12 : 0.08);

  return (
    <Box
      sx={{
        borderBottom: '1px solid',
        borderColor: 'primary.tableBorder',
      }}
    >
      <Box
        onClick={onToggle}
        sx={(t) => ({
          alignItems: 'center',
          bgcolor: selected
            ? t.palette.mode === 'light'
              ? 'rgba(59,44,147,0.06)'
              : 'rgba(155,124,255,0.12)'
            : baseBg ?? 'background.paper',
          cursor: 'pointer',
          display: 'flex',
          gap: 1.25,
          px: 1.5,
          py: 1.25,
        })}
      >
        <Checkbox checked={selected} onClick={onToggle} />
        <AudioButton word={word.word ?? ''} size={34} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack
            direction={'row'}
            alignItems={'baseline'}
            spacing={1}
            sx={{ flexWrap: 'wrap', rowGap: 0.5 }}
          >
            <Typography sx={{ fontSize: 16, fontWeight: 700 }}>
              {word.partOfSpeech === 'VERB' ? `to ${word.word}` : word.word}
            </Typography>
            {showTranscription && (
              <Typography
                sx={{
                  color: 'text.secondary',
                  fontFamily: 'ui-monospace, monospace',
                  fontSize: 13,
                }}
              >
                {word.transcription}
              </Typography>
            )}
            <POSTag pos={word.partOfSpeech} />
          </Stack>
          <Typography
            sx={{ color: 'text.secondary', fontSize: 14, mt: 0.25 }}
          >
            {word.translation}
          </Typography>
        </Box>
        {status !== 'new' && (
          <StatusChip status={status} onClick={(e) => e.stopPropagation()} />
        )}
        <Box
          component={'button'}
          onClick={(e) => {
            e.stopPropagation();
            if (word.definition) setOpen((o) => !o);
          }}
          disabled={!word.definition}
          sx={{
            alignItems: 'center',
            background: 'transparent',
            border: 'none',
            borderRadius: '50%',
            color: 'text.secondary',
            cursor: word.definition ? 'pointer' : 'default',
            display: 'inline-flex',
            flex: 'none',
            height: 30,
            justifyContent: 'center',
            opacity: word.definition ? 1 : 0.3,
            width: 30,
          }}
        >
          <ExpandMoreIcon
            sx={{
              fontSize: 22,
              transform: open ? 'rotate(180deg)' : 'none',
              transition: 'transform .2s ease',
            }}
          />
        </Box>
      </Box>
      {open && word.definition && (
        <Box
          sx={{
            bgcolor:
              status === 'new'
                ? 'background.default'
                : hexToRgba(statusColor, isDark ? 0.08 : 0.05),
            pl: 7,
            pr: 2,
            py: 1.25,
          }}
        >
          {showDefinition && (
            <>
              <Typography
                sx={{
                  color: 'text.secondary',
                  fontSize: 11,
                  fontWeight: 800,
                  letterSpacing: '0.06em',
                  mb: 0.5,
                  textTransform: 'uppercase',
                }}
              >
                {'Визначення'}
              </Typography>
              <Typography sx={{ fontSize: 14, lineHeight: 1.45 }}>
                {word.definition}
              </Typography>
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

const StudyCard: React.FC<{
  word: DictionaryWord;
  selected: boolean;
  onToggle: () => void;
  showTranscription: boolean;
  showPartOfSpeech: boolean;
  showDefinition: boolean;
}> = ({
  word,
  selected,
  onToggle,
  showTranscription,
  showPartOfSpeech,
  showDefinition,
}) => {
  const theme = useTheme();
  const status = statusOf(word);
  const statusColor = pickStatusColor(status, theme.palette.mode);
  const isDark = theme.palette.mode === 'dark';

  // Tile background fully washed with the status colour at low opacity.
  // `new` words stay on the neutral paper surface (no signalling needed —
  // every new tile looks identical, and the absence of tint IS the cue).
  const baseBg =
    status === 'new'
      ? undefined
      : hexToRgba(statusColor, isDark ? 0.16 : 0.1);
  const hoverBg =
    status === 'new'
      ? undefined
      : hexToRgba(statusColor, isDark ? 0.22 : 0.15);

  return (
    <Box
      onClick={onToggle}
      sx={{
        bgcolor: baseBg ?? 'background.paper',
        border: '1px solid',
        borderColor: selected
          ? 'primary.main'
          : status === 'new'
          ? 'primary.tableBorder'
          : hexToRgba(statusColor, 0.3),
        borderRadius: 2,
        cursor: 'pointer',
        display: 'flex',
        gap: 1.75,
        p: '16px 18px',
        transition: 'background-color .14s ease, border-color .14s ease',
        '&:hover': {
          bgcolor: hoverBg ?? 'background.default',
          borderColor: 'primary.main',
        },
      }}
    >
      <Box sx={{ pt: '2px' }}>
        <Checkbox checked={selected} onClick={onToggle} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack
          direction={'row'}
          alignItems={'center'}
          spacing={1.25}
          sx={{ flexWrap: 'wrap', mb: 0.625, rowGap: 0.75 }}
        >
          <AudioButton word={word.word ?? ''} size={34} />
          <Typography sx={{ fontSize: 19, fontWeight: 800 }}>
            {word.partOfSpeech === 'VERB' ? `to ${word.word}` : word.word}
          </Typography>
          {showTranscription && (
            <Typography
              sx={{
                color: 'text.secondary',
                fontFamily: 'ui-monospace, monospace',
                fontSize: 14,
              }}
            >
              {word.transcription}
            </Typography>
          )}
          {showPartOfSpeech && <POSTag pos={word.partOfSpeech} />}
          <Box sx={{ flex: 1 }} />
          {status !== 'new' && (
            <StatusChip status={status} onClick={(e) => e.stopPropagation()} />
          )}
        </Stack>
        <Typography sx={{ fontSize: 16, fontWeight: 600, mb: 0.625 }}>
          {word.translation}
        </Typography>
        {showDefinition && word.definition && (
          <Typography
            sx={{ color: 'text.secondary', fontSize: 14, lineHeight: 1.45 }}
          >
            {word.definition}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const EmptyState: React.FC = () => (
  <Box
    sx={{
      bgcolor: 'background.paper',
      border: '1px solid',
      borderColor: 'primary.tableBorder',
      borderRadius: 2,
      color: 'text.secondary',
      px: 3,
      py: 5,
      textAlign: 'center',
    }}
  >
    {'Нічого не знайдено за цим фільтром'}
  </Box>
);

/**
 * Builds a `{ letter → [ids] }` map so the GroupHeader can know how many
 * words belong to each letter — for the "N слів · M обрано" counter and
 * for group-level toggle.
 */
const buildLetterGroups = (words: DictionaryWord[]) => {
  const map = new Map<string, number[]>();
  words.forEach((w) => {
    const letter = (w.word?.[0] ?? '').toUpperCase();
    if (!letter) return;
    const arr = map.get(letter) ?? [];
    arr.push(w.id);
    map.set(letter, arr);
  });
  return map;
};

const DictionaryWordsView: React.FC<DictionaryWordsViewProps> = ({
  view,
  columns,
}) => {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const visibleCols = useMemo(() => buildVisibleCols(columns), [columns]);
  const template = useMemo(
    () => visibleCols.map((c) => c.width).join(' '),
    [visibleCols],
  );
  const minWidth = useMemo(() => tableMinWidth(visibleCols), [visibleCols]);
  const letterGroups = useMemo(
    () => buildLetterGroups(view.filtered),
    [view.filtered],
  );

  if (view.filtered.length === 0) {
    return <EmptyState />;
  }

  if (view.viewMode === 'cards') {
    const showTranscription = columns?.transcription ?? true;
    const showPartOfSpeech = columns?.partOfSpeech ?? true;
    const showDefinition = columns?.definition ?? true;
    // Phone forces 1 col regardless of preference — 2/3 cols at <480px
    // squashes the card content into unreadable strips.
    const perRow = isPhone ? 1 : view.cardsPerRow;
    return (
      <Box
        sx={{
          display: 'grid',
          gap: 1.5,
          gridTemplateColumns: `repeat(${perRow}, 1fr)`,
        }}
      >
        {view.filtered.map((w) => (
          <StudyCard
            key={w.id}
            word={w}
            selected={!!view.selected[w.id]}
            onToggle={() => view.toggle(w.id)}
            showTranscription={showTranscription}
            showPartOfSpeech={showPartOfSpeech}
            showDefinition={showDefinition}
          />
        ))}
      </Box>
    );
  }

  // ── Phone (xs) → stacked MobileRow list, no horizontal scroll ──
  if (isPhone) {
    const showTranscription = columns?.transcription ?? true;
    const showDefinition = columns?.definition ?? true;
    return (
      <Box
        sx={{
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'primary.tableBorder',
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        {view.filtered.map((w, i) => {
          const prev = view.filtered[i - 1];
          const letter = (w.word?.[0] ?? '').toUpperCase();
          const showGroup =
            !!letter &&
            (!prev || (prev.word?.[0] ?? '').toUpperCase() !== letter);
          return (
            <Fragment key={w.id}>
              {showGroup && (
                <GroupHeader
                  letter={letter}
                  ids={letterGroups.get(letter) ?? []}
                  view={view}
                />
              )}
              <MobileRow
                word={w}
                selected={!!view.selected[w.id]}
                onToggle={() => view.toggle(w.id)}
                showTranscription={showTranscription}
                showDefinition={showDefinition}
              />
            </Fragment>
          );
        })}
      </Box>
    );
  }

  // ── Tablet / Desktop → table with horizontal scroll fallback ──
  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'primary.tableBorder',
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      <Box sx={{ overflowX: 'auto' }}>
        <Box sx={{ minWidth }}>
          <HeaderRow view={view} cols={visibleCols} template={template} />
          {view.filtered.map((w, i) => {
            const prev = view.filtered[i - 1];
            const letter = (w.word?.[0] ?? '').toUpperCase();
            const showGroup =
              !!letter &&
              (!prev || (prev.word?.[0] ?? '').toUpperCase() !== letter);
            return (
              <Fragment key={w.id}>
                {showGroup && (
                  <GroupHeader
                    letter={letter}
                    ids={letterGroups.get(letter) ?? []}
                    view={view}
                  />
                )}
                <TableRow
                  word={w}
                  index={i}
                  selected={!!view.selected[w.id]}
                  onToggle={() => view.toggle(w.id)}
                  cols={visibleCols}
                  template={template}
                />
              </Fragment>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default DictionaryWordsView;
